/**
 * OddsComparisonModal Component
 * Shows all sportsbooks' odds for a specific prop, ranked by payout.
 * Opens when the user clicks a BestLineBadge on a leg.
 * Optionally allows adding the prop to the parlay builder from any sportsbook row.
 */

import { SPORTSBOOKS } from '@config/constants';
import { formatTimeAgo } from '@parlay-picker/shared/formatting';
import {
  type BestOddsResult,
  type BookOdds,
  oldestBookUpdate,
} from '@parlay-picker/shared/oddsUtils';
import { getBookDisplayName, getSportsbookIdByOddsApiKey } from '@parlay-picker/shared/sportsbook';
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import { Check, Plus, X } from 'lucide-react';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

// =============================================================================
// Types
// =============================================================================

interface OddsComparisonModalProps {
  comparison: BestOddsResult;
  onClose: () => void;
  /** When provided, each book row shows an "Add" button to add the prop from that sportsbook */
  onAddFromBook?: (book: BookOdds) => void;
  /** Sportsbook currently locked in the builder — rows from other books are disabled */
  builderSportsbook?: string | null;
  /** Whether this prop is already in the builder (shows checkmark instead of +) */
  isInBuilder?: boolean;
}

// =============================================================================
// Helpers
// =============================================================================

/** Calculate payout on a $100 bet given decimal odds */
function calculatePayout(decimalOdds: number): string {
  return (decimalOdds * 100).toFixed(2);
}

/** Calculate savings between two decimal odds on a $100 bet */
function calculateSavings(bestDecimal: number, currentDecimal: number): string {
  const diff = (bestDecimal - currentDecimal) * 100;
  return diff.toFixed(2);
}

/** Format the market + direction into a readable label */
function formatPropLabel(comparison: BestOddsResult): string {
  const line = comparison.line !== undefined ? ` ${comparison.line}` : '';
  return `${comparison.direction}${line} ${formatStatLabel(comparison.market)}`;
}

// =============================================================================
// Row Component
// =============================================================================

function BookRow({
  book,
  isBest,
  isCurrentBook,
  onAdd,
  isDisabled,
  disabledReason,
  isAdded,
}: {
  book: BookOdds;
  isBest: boolean;
  isCurrentBook: boolean;
  onAdd?: () => void;
  isDisabled?: boolean;
  disabledReason?: string;
  isAdded?: boolean;
}): ReactElement {
  const config = SPORTSBOOKS[getSportsbookIdByOddsApiKey(book.sportsbook) ?? book.sportsbook];
  const name = getBookDisplayName(book.sportsbook);
  const dotColor = config?.dot || 'bg-theme-surface-hover';
  const payout = calculatePayout(book.decimalOdds);

  return (
    <div
      className={`flex items-center justify-between px-3 py-2 rounded-lg ${
        isBest ? 'bg-theme-success/30 ring-1 ring-theme-success/30' : 'bg-theme-surface/50'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${dotColor}`} />
        <span
          className={`text-sm font-medium ${isBest ? 'text-theme-success' : 'text-theme-text'}`}
        >
          {name}
        </span>
        {isBest && (
          <span className="text-[10px] uppercase tracking-wide text-theme-success font-semibold">
            Best
          </span>
        )}
        {isCurrentBook && (
          <span className="text-[10px] uppercase tracking-wide text-theme-text-tertiary font-semibold">
            Your book
          </span>
        )}
        {book.isSharp && (
          <span className="text-[10px] uppercase tracking-wide text-theme-text-tertiary font-semibold">
            Sharp ref
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className={`text-sm font-mono ${isBest ? 'text-theme-success' : 'text-theme-text'}`}>
          {book.odds}
        </span>
        <span className="text-xs text-theme-text-tertiary w-16 text-right">${payout}</span>
        {onAdd !== undefined && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!isDisabled && !isAdded) onAdd();
            }}
            disabled={isDisabled || isAdded}
            className={`p-1.5 rounded-lg transition shrink-0 ${
              isAdded
                ? 'bg-theme-success/20 text-theme-success cursor-default'
                : isDisabled
                  ? 'bg-theme-surface/30 text-theme-text-tertiary cursor-not-allowed opacity-40'
                  : 'bg-theme-border/50 hover:bg-theme-success/30 text-theme-text-secondary hover:text-theme-success'
            }`}
            title={
              isAdded
                ? 'Already in builder'
                : isDisabled
                  ? disabledReason || 'Cannot add from this book'
                  : `Add from ${name}`
            }
          >
            {isAdded ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Modal Component
// =============================================================================

export function OddsComparisonModal({
  comparison,
  onClose,
  onAddFromBook,
  builderSportsbook,
  isInBuilder,
}: OddsComparisonModalProps): ReactElement {
  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const savings = comparison.hasBetterLine
    ? calculateSavings(comparison.bestBook.decimalOdds, comparison.currentBook.decimalOdds)
    : null;

  const currentBookName = getBookDisplayName(comparison.currentBook.sportsbook);
  const bestBookName = getBookDisplayName(comparison.bestBook.sportsbook);
  const linesUpdatedAt = oldestBookUpdate(comparison.allBooks);

  // Portaled: the picks parlay card scales on hover, which makes it the containing block for `fixed`
  // and put the modal under the sticky header.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-theme-surface border border-theme-border rounded-xl shadow-2xl max-w-sm w-full animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-theme-border">
          <div className="min-w-0 mr-3">
            <h3 className="text-sm font-bold text-theme-text truncate">{comparison.player}</h3>
            <p className="text-xs text-theme-text-secondary">{formatPropLabel(comparison)}</p>
            {linesUpdatedAt && (
              <p className="text-[10px] text-theme-text-tertiary" data-testid="odds-comparison-age">
                Oldest line: {formatTimeAgo(linesUpdatedAt)}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-theme-text-secondary hover:text-theme-text transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Column headers */}
        <div className="flex items-center justify-between px-4 pt-3 pb-1">
          <span className="text-[10px] uppercase tracking-wider text-theme-text-tertiary font-semibold">
            Sportsbook
          </span>
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-wider text-theme-text-tertiary font-semibold">
              Odds
            </span>
            <span className="text-[10px] uppercase tracking-wider text-theme-text-tertiary font-semibold w-16 text-right">
              $100 Payout
            </span>
            {onAddFromBook && <span className="w-7.5" />}
          </div>
        </div>

        {/* Book rows: bettable soft books first (best highlighted), sharp books as reference below */}
        <div className="px-3 pb-3 space-y-1">
          {comparison.allBooks
            .filter((book) => !book.isSharp)
            .map((book) => {
              const isSportsbookMismatch =
                builderSportsbook != null &&
                book.sportsbook.toLowerCase() !== builderSportsbook.toLowerCase();

              return (
                <BookRow
                  key={book.sportsbook}
                  book={book}
                  isBest={book.sportsbook === comparison.bestBook.sportsbook}
                  isCurrentBook={book.sportsbook === comparison.currentBook.sportsbook}
                  onAdd={onAddFromBook ? () => onAddFromBook(book) : undefined}
                  isDisabled={isSportsbookMismatch}
                  disabledReason={
                    isSportsbookMismatch
                      ? `Builder is locked to ${SPORTSBOOKS[builderSportsbook!]?.name || builderSportsbook}`
                      : undefined
                  }
                  isAdded={isInBuilder}
                />
              );
            })}

          {comparison.allBooks.some((book) => book.isSharp) && (
            <>
              <p className="px-3 pt-2 text-[10px] uppercase tracking-wider text-theme-text-tertiary font-semibold">
                Sharp reference books
              </p>
              {comparison.allBooks
                .filter((book) => book.isSharp)
                .map((book) => (
                  <BookRow key={book.sportsbook} book={book} isBest={false} isCurrentBook={false} />
                ))}
            </>
          )}
        </div>

        {/* Savings footer */}
        {savings && parseFloat(savings) > 0 && (
          <div className="px-4 py-3 border-t border-theme-border bg-theme-success/10">
            <p className="text-xs text-theme-success">
              Save ${savings} per $100 bet on <span className="font-semibold">{bestBookName}</span>{' '}
              vs {currentBookName}
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
